import { DEMO_FALLBACK_URL } from "../seo/siteMeta";

/**
 * Marketing-site half of the prospect funnel.
 *
 * Events go to the sandbox's own ingestion endpoint rather than a separate analytics store, so
 * "clicked the demo CTA on badgereg.com" and "explored the owner console for twelve minutes" land in
 * one place and can be read as a single journey. Splitting them across two systems would mean
 * manually correlating timestamps to answer the only question that matters: did the site actually
 * send us anyone.
 *
 * Same privacy posture as the demo: a random per-tab session id, an allowlisted event name, no PII,
 * no cookies. That is also why this uses fetch directly instead of an analytics SDK: there is no
 * third party involved and nothing to consent to.
 */

const SESSION_KEY = "mb_demo_session_id";

export function getDemoUrl() {
  const configured = window?._env_?.REACT_APP_DEMO_URL;
  // The template leaves an unsubstituted "${REACT_APP_DEMO_URL}" when the env var is missing, which
  // would otherwise be used verbatim as a URL and 404.
  if (configured && !configured.startsWith("${")) {
    return configured;
  }
  return DEMO_FALLBACK_URL;
}

/**
 * The URL to embed or open. The `src=marketing` marker is how the demo distinguishes a visitor who
 * came from here from one who typed the address in, which is precisely the number that says whether
 * the marketing site is doing its job.
 */
export function getDemoUrlWithSource() {
  const base = getDemoUrl();
  return `${base}${base.includes("?") ? "&" : "?"}src=marketing`;
}

/**
 * Shares sessionStorage with the demo under the same key, but does NOT share the value: the demo is a
 * different origin, so it will mint its own. The `src=marketing` parameter is what links the two, not
 * a shared identifier, which keeps this from becoming cross-site tracking.
 */
/**
 * A random 32-character id.
 *
 * `crypto.randomUUID` is only defined in a secure context and only in reasonably recent browsers, so
 * relying on it alone meant anything served over plain HTTP, and older Safari, threw here and got no
 * session id, which this module treats as "do not track". Traffic from those visitors was therefore
 * missing entirely rather than merely approximate, and silently so.
 *
 * These ids label an anonymous session for a fortnight at most; they are not a security token, so a
 * non-cryptographic fallback is an appropriate way to keep counting.
 */
function randomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  let out = "";
  while (out.length < 32) {
    out += Math.random().toString(16).slice(2);
  }
  return out.slice(0, 32);
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private-mode Safari has historically thrown on sessionStorage writes. Losing the count is
    // acceptable; breaking the page over analytics is not.
    return null;
  }
}

function getDemoApiUrl() {
  const configured = window?._env_?.REACT_APP_DEMO_API_URL;
  if (configured && !configured.startsWith("${")) {
    return configured.replace(/\/$/, "");
  }
  return null;
}

/**
 * Fire-and-forget. Uses `keepalive` so an event fired immediately before navigating away to the demo
 * still gets sent; without it, the browser cancels the request on unload and the CTA click, which is
 * the single most important marketing event, would be the one we systematically lost.
 */
export function trackMarketingEvent(eventName) {
  const apiUrl = getDemoApiUrl();
  const sessionId = getSessionId();
  if (!apiUrl || !sessionId) {
    return Promise.resolve();
  }
  try {
    return fetch(`${apiUrl}/demo/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        event: eventName,
        source: "marketing",
      }),
      keepalive: true,
      // No cookies: this endpoint is anonymous and sending credentials cross-origin would only
      // invite a CORS preflight failure.
      credentials: "omit",
    }).catch(() => {});
  } catch {
    return Promise.resolve();
  }
}

export const MARKETING_EVENTS = {
  DEMO_CTA_CLICKED: "marketing_demo_cta_clicked",
  CONTACT_SUBMITTED: "marketing_contact_submitted",
};

/**
 * Route to page-view event name.
 *
 * The ingestion endpoint takes an allowlisted event name and nothing else (no path, no properties
 * bag), so the page has to be encoded in the name. That is a deliberate privacy constraint on the
 * server side, not an oversight: there is no field here for a tampered client to smuggle a query
 * string, referrer or user identifier into.
 *
 * Consequence worth knowing: a route added without a matching entry here is simply not counted. The
 * server would reject an unknown name anyway, so silence is the correct failure mode.
 */
const PAGE_VIEW_EVENTS = {
  "/": "marketing_home_viewed",
  "/pricing": "marketing_pricing_viewed",
  "/demo": "marketing_demo_page_viewed",
  "/faqs": "marketing_faqs_viewed",
  "/contact": "marketing_contact_viewed",
  "/about": "marketing_about_viewed",
  "/security": "marketing_security_viewed",
  "/privacy": "marketing_privacy_viewed",
  "/terms": "marketing_terms_viewed",
};

/**
 * Submits a lead to the API, which persists it, posts it to Slack, and counts it.
 *
 * Returns true only when the API confirmed it. The caller needs to know, because the fallback path
 * (posting straight to a Slack webhook from the browser) is the difference between a lead that is
 * merely un-tidy and one that is lost.
 */
export async function submitLead(fields) {
  const apiUrl = getDemoApiUrl();
  if (!apiUrl) {
    return false;
  }
  const response = await fetch(`${apiUrl}/demo/lead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...fields,
      sessionId: getSessionId(),
      source: "marketing",
    }),
    credentials: "omit",
  });
  // 429 means the rate limiter already has a recent submission from this visitor. Treating that as
  // success is correct: another copy in Slack helps nobody, and the prospect should not be told their
  // message failed when we in fact already have it.
  if (response.status === 429) {
    return true;
  }
  if (!response.ok) {
    throw new Error(`Lead submission failed with ${response.status}`);
  }
  return true;
}

/**
 * Records a page view, if the path is one we count.
 *
 * Trailing slashes are normalised because `/pricing` and `/pricing/` are the same page to a visitor
 * and would otherwise be one counted view and one silent miss.
 */
export function trackPageView(pathname) {
  const path = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const event = PAGE_VIEW_EVENTS[path || "/"];
  if (!event) {
    return Promise.resolve();
  }
  return trackMarketingEvent(event);
}
